import React from "react";
import { format } from "date-fns";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const TOTAL_STEPS = 3;

const getCurrentStep = (
  checkinStatus?: CheckinStatus,
  checkoutStatus?: CheckoutStatus,
) => {
  if (
    checkinStatus === CheckinStatus.DONE &&
    checkoutStatus === CheckoutStatus.DONE
  )
    return 3;

  if (checkinStatus === CheckinStatus.DONE) return 2;

  return 1;
};

const ReservationTimeline = ({
  bookingSource,
  arrivalDate,
  departureDate,
  checkinStatus,
  checkoutStatus,
}: {
  bookingSource: string;
  arrivalDate: string;
  departureDate: string;
  checkinStatus?: CheckinStatus;
  checkoutStatus?: CheckoutStatus;
}) => {
  const { t } = useTranslation();
  const currentStep = getCurrentStep(checkinStatus, checkoutStatus);
  const fillPercent = ((currentStep - 1) / (TOTAL_STEPS - 1)) * 100;
  const isCheckinDone = checkinStatus === CheckinStatus.DONE;
  const isCheckoutDone = checkoutStatus === CheckoutStatus.DONE;

  const checkInDotClass = (() => {
    if (!isCheckinDone) return "bg-neutral-200";
    if (isCheckinDone && isCheckoutDone) return "bg-indigo";
    return "bg-neutral-200 border-2 border-violet";
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 h-2 relative w-full rounded-full bg-neutral-200">
          <div
            className={cn(
              "absolute left-0 top-0 h-2 rounded-full",
              isCheckinDone
                ? "bg-indigo"
                : "bg-neutral-200 border-2 border-violet",
            )}
            style={{ width: `${fillPercent}%` }}
          />
          <div
            className={cn(
              "absolute left-0 top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full",
              isCheckinDone
                ? "bg-indigo"
                : "bg-neutral-200 border-2 border-violet",
            )}
          />
          <div
            className={cn(
              "absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full",
              checkInDotClass,
            )}
          />
          <div
            className={cn(
              "absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full",
              isCheckoutDone
                ? "bg-neutral-200 border-2 border-violet"
                : "bg-neutral-200",
            )}
          />
        </div>
      </div>
      <div className="flex text-xs">
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-neutral-500">
            {t("reservation.timeline.booked")}
          </span>
          <span className="text-zinc/50">{bookingSource}</span>
        </div>
        <div className="flex-1 flex flex-col gap-1 text-center">
          <span className="text-neutral-500">
            {t("reservation.timeline.checkIn")}
          </span>
          <span className="text-zinc">
            {arrivalDate ? format(new Date(arrivalDate), "dd.MM.yyyy") : ""}
          </span>
        </div>
        <div className="flex-1 flex flex-col gap-1 text-right">
          <span className="text-neutral-500">
            {t("reservation.timeline.checkOut")}
          </span>
          <span className="text-zinc">
            {departureDate ? format(new Date(departureDate), "dd.MM.yyyy") : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReservationTimeline;
